import { useCallback, useEffect, useState } from 'react';
import Parse from '../api/config';
import { usePermissions } from '../hooks/usePermissions';
import {
    Banner,
    Button,
    Card,
    CardHeader,
    EmptyState,
    Modal,
    PageBody,
    PageHeader,
    Spinner,
    cx,
} from './ui';

/**
 * Editor for the public website (bethelchristiancenter.com). Everything here
 * reads and writes through the website cloud functions; the public site picks
 * up changes on its next page load — no deploy involved.
 */

const TABS = [
    { key: 'settings', label: 'Site settings' },
    { key: 'service', label: 'Service times' },
    { key: 'event', label: 'Events' },
    { key: 'pastor', label: 'Pastors' },
    { key: 'belief', label: 'Beliefs' },
    { key: 'messages', label: 'Messages' },
];

const Tabs = ({ active, onChange }) => (
    <div className="-mb-px flex gap-1 overflow-x-auto">
        {TABS.map((tab) => (
            <button
                key={tab.key}
                type="button"
                onClick={() => onChange(tab.key)}
                className={cx(
                    'whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium transition-colors',
                    active === tab.key
                        ? 'border-brand-700 text-brand-800'
                        : 'border-transparent text-neutral-500 hover:border-neutral-300 hover:text-neutral-800'
                )}
            >
                {tab.label}
            </button>
        ))}
    </div>
);

const inputClass =
    'w-full rounded-md border border-neutral-300 px-3 py-2 text-sm text-neutral-900 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500';

const Field = ({ label, hint, children }) => (
    <label className="block">
        <span className="mb-1 block text-sm font-medium text-neutral-700">{label}</span>
        {children}
        {hint && <span className="mt-1 block text-xs text-neutral-400">{hint}</span>}
    </label>
);

/** Upload an image to Parse and hand back its URL. */
const PhotoField = ({ label, value, onChange }) => {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    const upload = async (file) => {
        if (!file) return;
        setBusy(true);
        setError('');
        try {
            const safeName = file.name.replace(/[^a-zA-Z0-9.\-_]/g, '_');
            const parseFile = new Parse.File(safeName, file);
            await parseFile.save();
            onChange(parseFile.url());
        } catch (e) {
            setError(e.message || 'Upload failed');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div>
            <span className="mb-1 block text-sm font-medium text-neutral-700">{label}</span>
            <div className="flex items-center gap-3">
                {value ? (
                    <img src={value} alt="" className="h-16 w-16 rounded-md border border-neutral-200 object-cover" />
                ) : (
                    <div className="flex h-16 w-16 items-center justify-center rounded-md border border-dashed border-neutral-300 text-xs text-neutral-400">
                        None
                    </div>
                )}
                <div className="space-y-1">
                    <input
                        type="file"
                        accept="image/*"
                        disabled={busy}
                        onChange={(e) => upload(e.target.files[0])}
                        className="block text-sm text-neutral-600 file:mr-3 file:rounded-md file:border-0 file:bg-neutral-100 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-neutral-700 hover:file:bg-neutral-200"
                    />
                    {value && (
                        <button type="button" onClick={() => onChange('')} className="text-xs text-neutral-500 underline">
                            Remove photo
                        </button>
                    )}
                    {busy && <span className="text-xs text-neutral-400">Uploading…</span>}
                    {error && <span className="text-xs text-red-600">{error}</span>}
                </div>
            </div>
        </div>
    );
};

/* ------------------------------------------------------------ settings tab */

const SettingsTab = ({ settings, onSaved }) => {
    const [draft, setDraft] = useState(settings);
    const [saving, setSaving] = useState(false);
    const [status, setStatus] = useState(null);

    useEffect(() => setDraft(settings), [settings]);

    const set = (key) => (value) => setDraft((d) => ({ ...d, [key]: value }));
    const setFromEvent = (key) => (e) => set(key)(e.target.value);

    const save = async () => {
        setSaving(true);
        setStatus(null);
        try {
            await Parse.Cloud.run('saveWebsiteSettings', { settings: draft });
            setStatus({ tone: 'success', text: 'Saved. The website will show this on its next load.' });
            onSaved(draft);
        } catch (e) {
            setStatus({ tone: 'danger', text: e.message });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-4">
            <Card>
                <CardHeader
                    title="Announcement bar"
                    description="The strip across the very top of every page of the website."
                />
                <div className="space-y-4 p-5">
                    <label className="flex items-center gap-2 text-sm text-neutral-700">
                        <input
                            type="checkbox"
                            checked={!!draft.showAnnouncement}
                            onChange={(e) => set('showAnnouncement')(e.target.checked)}
                            className="h-4 w-4 rounded border-neutral-300 text-brand-700 focus:ring-brand-500"
                        />
                        Show the announcement bar
                    </label>
                    <Field label="Announcement text">
                        <input className={inputClass} value={draft.announcementText || ''} onChange={setFromEvent('announcementText')} />
                    </Field>
                </div>
            </Card>

            <Card>
                <CardHeader title="Contact & links" description="Shown in the header, footer, and contact page." />
                <div className="grid gap-4 p-5 sm:grid-cols-2">
                    <Field label="Address line 1">
                        <input className={inputClass} value={draft.addressLine1 || ''} onChange={setFromEvent('addressLine1')} />
                    </Field>
                    <Field label="Address line 2">
                        <input className={inputClass} value={draft.addressLine2 || ''} onChange={setFromEvent('addressLine2')} />
                    </Field>
                    <Field label="Phone">
                        <input className={inputClass} value={draft.phone || ''} onChange={setFromEvent('phone')} />
                    </Field>
                    <Field label="Service times (short)" hint="One line, shown on the contact page.">
                        <input className={inputClass} value={draft.serviceTimesShort || ''} onChange={setFromEvent('serviceTimesShort')} />
                    </Field>
                    <Field label="YouTube channel URL">
                        <input className={inputClass} value={draft.youtubeUrl || ''} onChange={setFromEvent('youtubeUrl')} />
                    </Field>
                    <Field label="Online giving URL" hint="Where the Give buttons send people. Leave empty until online giving is set up.">
                        <input className={inputClass} value={draft.givingUrl || ''} onChange={setFromEvent('givingUrl')} />
                    </Field>
                    <Field label="Directions URL">
                        <input className={inputClass} value={draft.directionsUrl || ''} onChange={setFromEvent('directionsUrl')} />
                    </Field>
                    <Field label="Contact form recipient" hint="Email address that receives contact-form messages.">
                        <input className={inputClass} value={draft.contactRecipient || ''} onChange={setFromEvent('contactRecipient')} />
                    </Field>
                </div>
            </Card>

            <Card>
                <CardHeader title="Photos" description="These replace the placeholder art on the website." />
                <div className="grid gap-4 p-5 sm:grid-cols-2">
                    <PhotoField label="Home hero (worship / congregation)" value={draft.heroPhotoUrl} onChange={set('heroPhotoUrl')} />
                    <PhotoField label="Welcome / lobby" value={draft.welcomePhotoUrl} onChange={set('welcomePhotoUrl')} />
                    <PhotoField label="Pastors (home page)" value={draft.pastorsPhotoUrl} onChange={set('pastorsPhotoUrl')} />
                    <PhotoField label="Map / location" value={draft.mapPhotoUrl} onChange={set('mapPhotoUrl')} />
                </div>
            </Card>

            {status && (
                <Banner tone={status.tone} title={status.text} />
            )}
            <div className="flex justify-end">
                <Button variant="primary" onClick={save} disabled={saving}>
                    {saving ? 'Saving…' : 'Save settings'}
                </Button>
            </div>
        </div>
    );
};

/* ---------------------------------------------------------------- item tabs */

// What each list edits, and how its rows are summarized in the table.
const ITEM_CONFIG = {
    service: {
        title: 'Service times',
        description: 'The weekly gatherings listed on the home page.',
        fields: [
            { key: 'day', label: 'Day', type: 'text' },
            { key: 'time', label: 'Time', type: 'text' },
            { key: 'name', label: 'Name', type: 'text' },
            { key: 'description', label: 'Description', type: 'textarea' },
        ],
        columns: ['day', 'time', 'name'],
        sortable: true,
    },
    event: {
        title: 'Events',
        description: 'Upcoming events shown on the calendar page. Past events drop off the public site automatically.',
        fields: [
            { key: 'title', label: 'Title', type: 'text' },
            { key: 'eventDate', label: 'Date', type: 'date' },
            { key: 'time', label: 'Time (as shown)', type: 'text', hint: 'e.g. 6:30 PM' },
            { key: 'location', label: 'Location', type: 'text' },
            { key: 'detailsUrl', label: 'Details link (optional)', type: 'text' },
        ],
        columns: ['title', 'eventDate', 'time', 'location'],
        sortable: false,
    },
    pastor: {
        title: 'Pastors',
        description: 'The leadership shown on the pastors page.',
        fields: [
            { key: 'name', label: 'Name', type: 'text' },
            { key: 'role', label: 'Role / title', type: 'text' },
            { key: 'bio', label: 'Bio', type: 'textarea' },
            { key: 'photoUrl', label: 'Portrait', type: 'photo' },
        ],
        columns: ['name', 'role'],
        sortable: true,
    },
    belief: {
        title: 'Beliefs',
        description: 'The numbered convictions on the What We Believe page.',
        fields: [
            { key: 'title', label: 'Title', type: 'text' },
            { key: 'body', label: 'Body', type: 'textarea' },
        ],
        columns: ['title'],
        sortable: true,
    },
};

const formatCell = (item, key) => {
    if (key === 'eventDate') {
        return item.eventDate ? new Date(item.eventDate).toLocaleDateString() : '—';
    }
    return item[key] || '—';
};

const ItemModal = ({ type, item, onClose, onSaved }) => {
    const config = ITEM_CONFIG[type];
    const [draft, setDraft] = useState(() => {
        const initial = {};
        for (const field of config.fields) {
            initial[field.key] = field.key === 'eventDate' && item?.eventDate
                ? new Date(item.eventDate).toISOString().slice(0, 10)
                : (item?.[field.key] ?? '');
        }
        return initial;
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const save = async () => {
        setSaving(true);
        setError('');
        try {
            const fields = { ...draft };
            if ('eventDate' in fields && fields.eventDate) {
                // Store noon local time so the calendar chip never lands on the
                // previous day when converted between timezones.
                fields.eventDate = new Date(`${fields.eventDate}T12:00:00`).toISOString();
            }
            if (item?.sortOrder !== undefined) fields.sortOrder = item.sortOrder;
            await Parse.Cloud.run('saveWebsiteItem', { type, id: item?.id, fields });
            onSaved();
        } catch (e) {
            setError(e.message);
            setSaving(false);
        }
    };

    return (
        <Modal
            open
            onClose={onClose}
            title={item?.id ? `Edit ${config.title.toLowerCase().replace(/s$/, '')}` : `Add to ${config.title.toLowerCase()}`}
            footer={
                <>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button variant="primary" onClick={save} disabled={saving}>
                        {saving ? 'Saving…' : 'Save'}
                    </Button>
                </>
            }
        >
            <div className="space-y-4">
                {config.fields.map((field) => (
                    field.type === 'photo' ? (
                        <PhotoField
                            key={field.key}
                            label={field.label}
                            value={draft[field.key]}
                            onChange={(value) => setDraft((d) => ({ ...d, [field.key]: value }))}
                        />
                    ) : (
                        <Field key={field.key} label={field.label} hint={field.hint}>
                            {field.type === 'textarea' ? (
                                <textarea
                                    rows={4}
                                    className={inputClass}
                                    value={draft[field.key]}
                                    onChange={(e) => setDraft((d) => ({ ...d, [field.key]: e.target.value }))}
                                />
                            ) : (
                                <input
                                    type={field.type}
                                    className={inputClass}
                                    value={draft[field.key]}
                                    onChange={(e) => setDraft((d) => ({ ...d, [field.key]: e.target.value }))}
                                />
                            )}
                        </Field>
                    )
                ))}
                {error && <Banner tone="danger" title={error} />}
            </div>
        </Modal>
    );
};

const ItemsTab = ({ type, items, onChanged }) => {
    const config = ITEM_CONFIG[type];
    const [editing, setEditing] = useState(null); // null | {} (new) | item
    const [workingId, setWorkingId] = useState(null);

    const remove = async (item) => {
        if (!window.confirm('Remove this from the website?')) return;
        setWorkingId(item.id);
        try {
            await Parse.Cloud.run('deleteWebsiteItem', { type, id: item.id });
            onChanged();
        } finally {
            setWorkingId(null);
        }
    };

    const move = async (index, delta) => {
        const other = index + delta;
        if (other < 0 || other >= items.length) return;
        const a = items[index];
        const b = items[other];
        setWorkingId(a.id);
        try {
            await Parse.Cloud.run('saveWebsiteItem', { type, id: a.id, fields: { sortOrder: other } });
            await Parse.Cloud.run('saveWebsiteItem', { type, id: b.id, fields: { sortOrder: index } });
            onChanged();
        } finally {
            setWorkingId(null);
        }
    };

    return (
        <Card>
            <CardHeader
                title={config.title}
                description={config.description}
                actions={<Button variant="primary" onClick={() => setEditing({})}>Add</Button>}
            />
            {items.length === 0 ? (
                <EmptyState title={`No ${config.title.toLowerCase()} yet`} description="Add the first one above." />
            ) : (
                <table className="min-w-full divide-y divide-neutral-200 text-sm">
                    <tbody className="divide-y divide-neutral-100">
                        {items.map((item, index) => (
                            <tr key={item.id} className={cx(workingId === item.id && 'opacity-40')}>
                                {config.columns.map((key) => (
                                    <td key={key} className="max-w-xs truncate px-4 py-3 text-neutral-800">
                                        {formatCell(item, key)}
                                    </td>
                                ))}
                                <td className="px-4 py-3 text-right">
                                    <div className="flex justify-end gap-2">
                                        {config.sortable && (
                                            <>
                                                <Button size="sm" onClick={() => move(index, -1)} disabled={index === 0}>↑</Button>
                                                <Button size="sm" onClick={() => move(index, 1)} disabled={index === items.length - 1}>↓</Button>
                                            </>
                                        )}
                                        <Button size="sm" onClick={() => setEditing(item)}>Edit</Button>
                                        <Button size="sm" onClick={() => remove(item)}>Remove</Button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
            {editing && (
                <ItemModal
                    type={type}
                    item={editing.id ? editing : null}
                    onClose={() => setEditing(null)}
                    onSaved={() => {
                        setEditing(null);
                        onChanged();
                    }}
                />
            )}
        </Card>
    );
};

/* ------------------------------------------------------------- messages tab */

const MessagesTab = () => {
    const [messages, setMessages] = useState(null);
    const [error, setError] = useState('');

    useEffect(() => {
        Parse.Cloud.run('listWebsiteContactMessages')
            .then(setMessages)
            .catch((e) => setError(e.message));
    }, []);

    if (error) return <Banner tone="danger" title={error} />;
    if (!messages) return <Card><EmptyState title="Loading messages…" /></Card>;
    if (!messages.length) {
        return <Card><EmptyState title="No messages yet" description="Messages sent through the website's contact form will appear here." /></Card>;
    }

    return (
        <div className="space-y-3">
            {messages.map((m) => (
                <Card key={m.id} className="p-4">
                    <div className="mb-1 flex flex-wrap items-baseline justify-between gap-2">
                        <div className="font-medium text-neutral-900">
                            {m.name} <a className="font-normal text-brand-700" href={`mailto:${m.email}`}>{m.email}</a>
                        </div>
                        <div className="text-xs text-neutral-400">{new Date(m.createdAt).toLocaleString()}</div>
                    </div>
                    <p className="whitespace-pre-wrap text-sm text-neutral-700">{m.message}</p>
                </Card>
            ))}
        </div>
    );
};

/* --------------------------------------------------------------------- page */

export const Website = () => {
    const { isAdmin, loading: permissionsLoading } = usePermissions();
    const [tab, setTab] = useState('settings');
    const [content, setContent] = useState(null);
    const [error, setError] = useState('');

    const load = useCallback(() => {
        Parse.Cloud.run('getWebsiteContent', { includeAllEvents: true })
            .then(setContent)
            .catch((e) => setError(e.message));
    }, []);

    useEffect(() => {
        if (isAdmin) load();
    }, [isAdmin, load]);

    const header = (
        <PageHeader title="Website" description="What the public sees at bethelchristiancenter.com.">
            {isAdmin && (
                <div className="mx-auto max-w-6xl px-4 pb-3 sm:px-6 lg:px-8">
                    <Tabs active={tab} onChange={setTab} />
                </div>
            )}
        </PageHeader>
    );

    if (permissionsLoading) {
        return (
            <>
                {header}
                <PageBody><Card><EmptyState title="Checking your access…" /></Card></PageBody>
            </>
        );
    }

    if (!isAdmin) {
        return (
            <>
                {header}
                <PageBody>
                    <Banner tone="danger" title="You don't have access to the website editor">
                        Editing the public website is limited to administrators.
                    </Banner>
                </PageBody>
            </>
        );
    }

    return (
        <>
            {header}
            <PageBody className="space-y-4">
                {error && <Banner tone="danger" title={error} />}
                {!content ? (
                    <div className="flex justify-center py-16"><Spinner className="h-6 w-6 text-brand-700" /></div>
                ) : (
                    <>
                        {tab === 'settings' && <SettingsTab settings={content.settings} onSaved={(s) => setContent((c) => ({ ...c, settings: s }))} />}
                        {tab === 'service' && <ItemsTab type="service" items={content.services} onChanged={load} />}
                        {tab === 'event' && <ItemsTab type="event" items={content.events} onChanged={load} />}
                        {tab === 'pastor' && <ItemsTab type="pastor" items={content.pastors} onChanged={load} />}
                        {tab === 'belief' && <ItemsTab type="belief" items={content.beliefs} onChanged={load} />}
                        {tab === 'messages' && <MessagesTab />}
                    </>
                )}
            </PageBody>
        </>
    );
};
